import * as React from 'react';
import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Button, Space } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import { checkForUpdate, reloadForUpdate } from '../../utils/VersionUtils';

interface VersionUpdateBannerProps {
	sticky?: boolean;
}

const INTERVAL_DURATION = 3 * 60 * 60 * 1000; // 3 hours

const VersionUpdateBanner: FC<VersionUpdateBannerProps> = ({ sticky = true }) => {
	const [updateAvailable, setUpdateAvailable] = useState<boolean>(false);
	const lastCheckTimeRef = useRef<number>(0);

	const checkVersion = useCallback(async () => {
		try {
			const result = await checkForUpdate();
			if (result && result.updateAvailable) {
				setUpdateAvailable(true);
			} else {
				setUpdateAvailable(false);
			}
		} catch (error) {
			console.error('Failed to check version:', error);
		}
	}, []);

	useEffect(() => {
		const maybeCheckVersion = () => {
			if (document.visibilityState !== 'visible') {
				return;
			}
			const now = Date.now();
			if (now - lastCheckTimeRef.current < INTERVAL_DURATION) {
				return;
			}
			lastCheckTimeRef.current = now;
			checkVersion();
		};

		maybeCheckVersion();

		const interval = setInterval(maybeCheckVersion, INTERVAL_DURATION);

		document.addEventListener('visibilitychange', maybeCheckVersion);

		return () => {
			clearInterval(interval);
			document.removeEventListener('visibilitychange', maybeCheckVersion);
		};
	}, [checkVersion]);

	const handleReload = () => {
		reloadForUpdate();
	};

	if (!updateAvailable) {
		return null;
	}

	const bannerStyle: React.CSSProperties = sticky
		? {
				margin: 0,
				borderRadius: 0,
				width: '100%',
		  }
		: {
				margin: '16px 0',
		  };

	return (
		<Alert
			message="New Version Available"
			description={
				<Space direction="vertical" size={8}>
					<div>
						A new version of OpenWrench is available. Please reload the page to get the latest
						features and improvements.
					</div>
				</Space>
			}
			type="info"
			showIcon
			action={
				<Button type="primary" icon={<ReloadOutlined />} onClick={handleReload}>
					Reload Now
				</Button>
			}
			style={bannerStyle}
		/>
	);
};

export default VersionUpdateBanner;
